import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  List,
  ListItem,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Close, Restore } from '@mui/icons-material';
import { format } from 'date-fns';

import {
  useEntityHistoryDialog,
  closeEntityHistoryDialog,
  setEntityHistoryLoading,
  setEntityHistoryData,
  setEntityHistoryError,
  HistoryEntry,
  useEntityHistoryDialogFieldType,
  applyHistoryVersion,
} from 'store/EntityHistory';
import { useBoundStore } from 'store';
import { documentsClient } from 'clients/documents/documentsClient';

export const EntityHistoryDialog: React.FC = () => {
  const {
    open,
    loading,
    error,
    data,
    entityType,
    entityId,
  } = useEntityHistoryDialog();
  const fieldType = useEntityHistoryDialogFieldType();

  const editLanguage = useBoundStore((state) => state.editLanguage);

  useEffect(() => {
    if (open && entityType && entityId && !loading) {
      // eslint-disable-next-line
			fetchHistory();
    }
    // eslint-disable-next-line
	}, [open, entityType, entityId, editLanguage]);

  const fetchHistory = async () => {
    if (!entityType || !entityId) return;

    setEntityHistoryLoading(true);

    try {
      const response = await documentsClient.getEntityHistory(entityType, entityId, editLanguage || 'nl');
      setEntityHistoryData(response.data);
    } catch (err) {
      setEntityHistoryError(
        err instanceof Error ? err.message : 'Failed to load history',
      );
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd-MM-yyyy');
    } catch {
      return dateString;
    }
  };

  const handleApplyVersion = (entry: HistoryEntry) => {
    const content = getContentToShow(entry);
    applyHistoryVersion(fieldType, content);
    closeEntityHistoryDialog();
  };

  const getContentToShow = (entry: HistoryEntry) => {
    if (fieldType === 'body' && entry.body !== undefined) {
      return entry.body;
    }
    return entry.title;
  };

  const renderHtmlContent = (content: string) => {
    if (!content) return '(empty)';

    return (
      <div
        style={{
          fontSize: '14px',
          lineHeight: '1.4',
          color: 'inherit',
        }}
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />
    );
  };

  const renderHistoryEntry = (entry: HistoryEntry) => {
    const content = getContentToShow(entry);

    return (
      <ListItem
        key={entry.id}
        sx={{
          flexDirection: 'column',
          alignItems: 'stretch',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}>
          <Typography variant="body2" fontWeight="bold">
            {entry.user}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {formatDate(entry.timestamp)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleApplyVersion(entry)}
              title="Apply this version"
            >
              <Restore fontSize="small"/>
            </IconButton>
          </Box>
        </Box>

        <Box sx={{
          color: 'text.primary',
          '& *': {
            fontSize: '14px !important',
            lineHeight: '1.4 !important',
            fontFamily: 'inherit !important',
            fontWeight: 'inherit !important',
            fontStyle: 'inherit !important',
            margin: '0 0 8px 0 !important',
            padding: '0 !important',
            color: 'inherit !important',
          },
          '& p': {
            marginBottom: '8px !important',
          },
          '& br': {
            lineHeight: '1.4 !important',
          },
          '& p:last-child': {
            marginBottom: '0 !important',
          },
          '& a': {
            textDecoration: 'underline',
            color: 'inherit !important',
          },
        }}>
          {content ? renderHtmlContent(content) : (
            <Typography variant="body2" color="text.secondary">
									(empty)
            </Typography>
          )}
        </Box>
      </ListItem>
    );
  };

  const handleClose = () => {
    closeEntityHistoryDialog();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '60vh',
          width: '500px',
        },
      }}
    >
      <DialogTitle sx={{
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography variant="h6" color="#222">
						Overzicht van wijzigingen
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close/>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress/>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {data && !loading && (
          <List sx={{ p: 0 }}>
            {data.history.length > 0 ? (
              data.history.map(renderHistoryEntry)
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
												Geen geschiedenis beschikbaar.
                </Typography>
              </Box>
            )}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};